import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Privacy Policy"
        description="CSW Privacy Policy. Learn how we collect, use, and protect your personal information when using our virtual study rooms and productivity tools."
        keywords="csw privacy policy, study room privacy, data protection"
        canonicalUrl="/privacy-policy/"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
              <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                Privacy Policy
              </span>
            </h1>
            <p className="text-lg text-muted-foreground">
              CSW – Chill Study Work App
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-8 md:p-12">
            <div className="prose prose-lg dark:prose-invert max-w-none">
              
              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Welcome to CSW – Chill Study Work App ("CSW," "we," "our," or "us"). We are committed to safeguarding your privacy and ensuring the protection of your personal information. This Privacy Policy outlines how we collect, use, share, and secure your information when you use our app.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">2. Types of Data Collected</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Personal Information:</strong> This includes your name, email address, and any other data provided during registration. We collect this data to create and manage your account and to provide you with the services offered by the app.</p>
                  <p><strong className="text-foreground">Usage Data:</strong> We automatically collect information about your interactions with the app, such as the features you use, the time spent on the app, and your activity patterns. This helps us improve the user experience.</p>
                  <p><strong className="text-foreground">Device Information:</strong> We collect information about the device you use to access the app, including IP address, operating system, and browser type.</p>
                  <p><strong className="text-foreground">Location Data:</strong> With your permission, we may collect and process location data to offer location-based services or content.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">3. How We Use Your Data</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">To Provide and Improve Our Services:</strong> We use your information to operate and enhance the app, ensuring a seamless user experience.</p>
                  <p><strong className="text-foreground">For Transaction Purposes:</strong> Your data is used to manage and process microtransactions within the app, including receiving payments from international clients.</p>
                  <p><strong className="text-foreground">Communication:</strong> We use your email address to send you notifications, updates, and other relevant communications related to the app. This includes verification emails to ensure the legitimacy of your account.</p>
                  <p><strong className="text-foreground">Security:</strong> We implement security measures to protect your data from unauthorized access, ensuring the integrity of our platform.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">4. Third-Party Services and Data Sharing</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Service Providers:</strong> We may share your information with trusted third-party service providers who assist us in operating the app, processing transactions, and providing additional services. These providers are obligated to protect your data to the same standards outlined in this policy.</p>
                  <p><strong className="text-foreground">Legal Compliance:</strong> We may disclose your data if required by law or in response to legal processes to protect our rights and interests.</p>
                  <p><strong className="text-foreground">Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your data may be transferred to the new entity.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">5. Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We employ a variety of security measures, including encryption and secure access controls, to protect your personal data from unauthorized access, alteration, or destruction.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">6. User Rights and Control</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Access and Update:</strong> You can access and update your personal information at any time through your account settings.</p>
                  <p><strong className="text-foreground">Email Verification:</strong> We send a verification email to ensure your email address is valid and not associated with spam or bot activity.</p>
                  <p><strong className="text-foreground">Data Retention:</strong> Your personal data is retained as long as necessary to provide the services or as required by law.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">7. Content Monitoring and Safety</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">AI Moderation:</strong> We use GetStream AI Moderation to monitor all chats and messages to prevent inappropriate content, including content related to underage protection, violence, politically sensitive topics, sexual content, gambling, and illegal activities.</p>
                  <p><strong className="text-foreground">User Reporting:</strong> Users can flag inappropriate content, which will be reviewed by our moderation team.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">8. Children's Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our services are not intended for children under the age of 13. We do not knowingly collect personal information from users under this age. If we become aware of such data being collected, we will take steps to delete it.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">9. International Users</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Your data may be processed in the country where it was collected and in other countries where laws regarding data processing may differ. By using our services, you consent to the transfer of your data to other countries as needed.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">10. Changes to This Privacy Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage you to review this policy periodically.
                </p>
              </div>

              <div className="mb-0">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">11. Contact Information</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  If you have any questions or concerns about this Privacy Policy, please contact us at:
                </p>
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                  <p className="font-semibold text-foreground mb-2">CSW – Chill Study Work App</p>
                  <a href="mailto:info@csw.live" className="text-primary hover:text-primary/80 transition-colors">
                    info@csw.live
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
